import { useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';

// Générer un ID de session unique
const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Détecter le type d'appareil
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Hook principal pour le tracking analytics
export const useAnalytics = (customerId, isActive = true) => {
  const sessionIdRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const lastPageRef = useRef(null);

  // Initialiser la session
  useEffect(() => {
    if (!isActive || !customerId) return;

    // Récupérer ou créer un session ID
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = generateSessionId();
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    sessionIdRef.current = sessionId;

    // Tracker le login
    trackEvent('login', { source: 'app' });

    // Heartbeat toutes les 2 minutes pour suivre la session active
    heartbeatIntervalRef.current = setInterval(() => {
      trackEvent('session_heartbeat', { page: lastPageRef.current });
    }, 120000); // 2 minutes

    // Cleanup
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      // Tracker le logout à la déconnexion
      trackEvent('logout', { sessionDuration: Date.now() - parseInt(sessionId.split('_')[1]) });
    };
  }, [customerId, isActive]);

  // Fonction pour tracker un événement
  const trackEvent = useCallback(async (eventType, eventData = {}) => {
    if (!customerId || !sessionIdRef.current) return;

    try {
      await api.trackAnalyticsEvent({
        customerId,
        eventType,
        eventData,
        deviceType: getDeviceType(),
        userAgent: navigator.userAgent,
        sessionId: sessionIdRef.current
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Analytics tracking failed:', error);
    }
  }, [customerId]);

  // Fonction pour tracker une page vue
  const trackPageView = useCallback((pageName, additionalData = {}) => {
    lastPageRef.current = pageName;
    trackEvent('page_view', { page: pageName, ...additionalData });
  }, [trackEvent]);

  // Fonction pour tracker une action
  const trackAction = useCallback((actionName, actionData = {}) => {
    trackEvent('action', { action: actionName, ...actionData });
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackAction,
    sessionId: sessionIdRef.current
  };
};

// Hook simplifié pour tracker une page au montage du composant
export const usePageTracking = (customerId, pageName, isActive = true) => {
  const { trackPageView } = useAnalytics(customerId, false); // Don't auto-track login

  useEffect(() => {
    if (isActive && customerId && pageName) {
      trackPageView(pageName);
    }
  }, [customerId, pageName, isActive, trackPageView]);
};

export default useAnalytics;
